// -----------------------------------------------------------------------------
// engine/media/source.ts — seekable media sources + the media manager.
//
// A MediaSource turns "what does this clip look like at source-time T?" into an
// ImageBitmap the compositor can draw. Two kinds:
//   • VideoMediaSource — asks the decode worker for frames, caches them, and
//     exposes a non-blocking peek() for the live preview plus an awaitable
//     getFrameExact() for export.
//   • ImageMediaSource — a single still bitmap (no worker needed).
//
// The MediaManager owns the one decode worker, routes its replies back to the
// right awaiting request, and imports files into assets + sources. This is the
// ONLY place the main thread touches the decode worker — the rest of the engine
// just asks a MediaSource for a bitmap. (Engine stays codec-blind, §4.)
//
// Tier 0 note: frames are decoded at NATIVE resolution (we pass a huge "proxy"
// box so the worker never downscales). Preview performance comes from rendering
// the compositor at a smaller canvas; a true low-res decode proxy is a Tier 1
// optimization that this protocol already leaves room for.
// -----------------------------------------------------------------------------
import { type ID, type MediaAsset, newId } from '../../core/model';
import type { DecodeResponse, MediaMeta } from '../../shared/worker-protocol';
import { FrameCache } from './frame-cache';

// Decode at native size in Tier 0 (see header note).
const NO_PROXY = 1 << 20;

export interface MediaSource {
  readonly assetId: ID;
  readonly meta: MediaMeta;
  /** Best frame available right now for the preview; schedules decode of the exact time. */
  peek(timeSec: number): ImageBitmap | null;
  /** Awaitable exact frame, used by the offline exporter. */
  getFrameExact(timeSec: number): Promise<ImageBitmap | null>;
  dispose(): void;
}

// A still image: always returns the same decoded bitmap.
class ImageMediaSource implements MediaSource {
  constructor(
    readonly assetId: ID,
    readonly meta: MediaMeta,
    private bitmap: ImageBitmap | null,
  ) {}
  peek(): ImageBitmap | null {
    return this.bitmap;
  }
  async getFrameExact(): Promise<ImageBitmap | null> {
    return this.bitmap;
  }
  dispose(): void {
    this.bitmap?.close();
    this.bitmap = null;
  }
}

// An audio-only source: no video frames. Sound plays through a hidden
// HTMLAudioElement managed by the MediaManager and driven by the player.
class AudioMediaSource implements MediaSource {
  constructor(
    readonly assetId: ID,
    readonly meta: MediaMeta,
  ) {}
  peek(): ImageBitmap | null {
    return null;
  }
  async getFrameExact(): Promise<ImageBitmap | null> {
    return null;
  }
  dispose(): void {
    /* the audio element is owned/cleaned up by the MediaManager */
  }
}

// A video: frames come from the decode worker, keyed by frame index and cached.
class VideoMediaSource implements MediaSource {
  private cache = new FrameCache(48);
  private inflight = new Map<number, Promise<ImageBitmap | null>>();
  private lastBitmap: ImageBitmap | null = null;

  constructor(
    readonly assetId: ID,
    readonly meta: MediaMeta,
    private readonly manager: MediaManager,
  ) {}

  private indexFor(timeSec: number): number {
    return Math.max(0, Math.round(timeSec * this.meta.fps));
  }
  private timeFor(index: number): number {
    return index / this.meta.fps;
  }

  peek(timeSec: number): ImageBitmap | null {
    const index = this.indexFor(timeSec);
    const cached = this.cache.get(String(index));
    if (cached) {
      this.lastBitmap = cached;
      return cached;
    }
    void this.fetch(index);
    return this.lastBitmap; // show the nearest frame we have until the exact one lands
  }

  getFrameExact(timeSec: number): Promise<ImageBitmap | null> {
    const index = this.indexFor(timeSec);
    const cached = this.cache.get(String(index));
    if (cached) return Promise.resolve(cached);
    return this.fetch(index);
  }

  // Single-flight decode for a given frame index.
  private fetch(index: number): Promise<ImageBitmap | null> {
    const existing = this.inflight.get(index);
    if (existing) return existing;
    const promise = this.manager
      .requestFrame(this.assetId, this.timeFor(index))
      .then((bmp) => {
        this.inflight.delete(index);
        if (bmp) {
          this.cache.set(String(index), bmp);
          this.lastBitmap = bmp;
          this.manager.onFrameReady?.(); // repaint the paused preview
        }
        return bmp;
      });
    this.inflight.set(index, promise);
    return promise;
  }

  dispose(): void {
    this.cache.clear();
    this.inflight.clear();
    this.lastBitmap = null;
    this.manager.closeMedia(this.assetId);
  }
}

type PendingResolver = (response: DecodeResponse) => void;

export class MediaManager {
  private worker: Worker;
  private sources = new Map<ID, MediaSource>();
  private pending = new Map<number, PendingResolver>();
  private reqCounter = 0;
  /** Called when a decoded frame lands, so a paused preview can repaint. */
  onFrameReady: (() => void) | null = null;
  // Hidden <audio> elements (one per audio-bearing asset) for preview sound,
  // plus their object URLs to revoke on cleanup. Driven by the player.
  private audioEls = new Map<ID, HTMLAudioElement>();
  private audioUrls = new Map<ID, string>();

  constructor() {
    // The decode worker URL is resolved by Vite; this is a URL reference, not a
    // module import, so the worker is bundled separately and type-checked under
    // its own (WebWorker) tsconfig.
    this.worker = new Worker(new URL('../../workers/decode.worker.ts', import.meta.url), {
      type: 'module',
    });
    this.worker.onmessage = (event: MessageEvent<DecodeResponse>) => {
      const msg = event.data;
      const resolver = this.pending.get(msg.reqId);
      if (resolver) {
        this.pending.delete(msg.reqId);
        resolver(msg);
      }
    };
  }

  getSource(assetId: ID): MediaSource | undefined {
    return this.sources.get(assetId);
  }

  // Import a File: probe it, build the asset metadata + a source, return the asset.
  async importFile(file: File): Promise<MediaAsset> {
    const mime = file.type || guessMimeFromName(file.name);
    const assetId = newId();

    if (mime.startsWith('audio/')) {
      const durationSec = await this.attachAudio(assetId, file);
      const meta: MediaMeta = { width: 0, height: 0, durationSec, fps: 30, hasAudio: true };
      this.sources.set(assetId, new AudioMediaSource(assetId, meta));
      return assetToRecord(assetId, file, 'audio', mime, meta);
    }

    if (mime.startsWith('image/')) {
      const bitmap = await createImageBitmap(file);
      const meta: MediaMeta = {
        width: bitmap.width,
        height: bitmap.height,
        durationSec: 0,
        fps: 30,
        hasAudio: false,
      };
      this.sources.set(assetId, new ImageMediaSource(assetId, meta, bitmap));
      return assetToRecord(assetId, file, 'image', mime, meta);
    }

    // Treat everything else as video (mp4/webm/mov/…). Open it in the worker.
    const meta = await this.openInWorker(assetId, file);
    this.sources.set(assetId, new VideoMediaSource(assetId, meta, this));
    if (meta.hasAudio) void this.attachAudio(assetId, file); // hidden <audio> for sound
    return assetToRecord(assetId, file, 'video', mime, meta);
  }

  // Create a hidden <audio> for an asset's sound; resolves with its duration (s).
  private attachAudio(assetId: ID, file: Blob): Promise<number> {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = url;
    // Keep it hidden in the DOM (more reliable playback across browsers).
    audio.style.display = 'none';
    audio.dataset.zfAudio = assetId;
    document.body.appendChild(audio);
    this.audioEls.set(assetId, audio);
    this.audioUrls.set(assetId, url);
    return new Promise<number>((resolve) => {
      audio.addEventListener(
        'loadedmetadata',
        () => resolve(Number.isFinite(audio.duration) ? audio.duration : 0),
        { once: true },
      );
      audio.addEventListener('error', () => resolve(0), { once: true });
    });
  }

  getAudioElement(assetId: ID): HTMLAudioElement | undefined {
    return this.audioEls.get(assetId);
  }
  audioEntries(): Array<[ID, HTMLAudioElement]> {
    return [...this.audioEls.entries()];
  }
  pauseAllAudio(): void {
    for (const audio of this.audioEls.values()) if (!audio.paused) audio.pause();
  }
  private releaseAudio(assetId: ID): void {
    const audio = this.audioEls.get(assetId);
    if (audio) {
      audio.pause();
      audio.removeAttribute('src');
      audio.load();
      audio.remove();
    }
    const url = this.audioUrls.get(assetId);
    if (url) URL.revokeObjectURL(url);
    this.audioEls.delete(assetId);
    this.audioUrls.delete(assetId);
  }

  // Re-register a source for an already-known asset (project load path). The
  // bytes come from scratch storage; we reuse the asset's existing id.
  async reopen(asset: MediaAsset, blob: Blob): Promise<void> {
    if (asset.kind === 'image') {
      const bitmap = await createImageBitmap(blob);
      const meta: MediaMeta = {
        width: asset.width,
        height: asset.height,
        durationSec: 0,
        fps: asset.fps,
        hasAudio: false,
      };
      this.sources.set(asset.id, new ImageMediaSource(asset.id, meta, bitmap));
      return;
    }
    if (asset.kind === 'audio') {
      const durationSec = await this.attachAudio(asset.id, blob);
      const meta: MediaMeta = {
        width: 0,
        height: 0,
        durationSec: durationSec || asset.durationSec,
        fps: 30,
        hasAudio: true,
      };
      this.sources.set(asset.id, new AudioMediaSource(asset.id, meta));
      return;
    }
    const meta = await this.openInWorker(asset.id, blob);
    this.sources.set(asset.id, new VideoMediaSource(asset.id, meta, this));
    if (meta.hasAudio) void this.attachAudio(asset.id, blob);
  }

  // ---- worker plumbing (used by VideoMediaSource) ----------------------------
  private openInWorker(mediaId: ID, file: Blob): Promise<MediaMeta> {
    return new Promise<MediaMeta>((resolve, reject) => {
      const reqId = ++this.reqCounter;
      this.pending.set(reqId, (response) => {
        if (response.type === 'opened' && response.ok && response.meta) resolve(response.meta);
        else reject(new Error(response.type === 'opened' ? response.error ?? 'open failed' : 'unexpected'));
      });
      this.worker.postMessage({
        type: 'open',
        reqId,
        mediaId,
        file,
        proxyWidth: NO_PROXY,
        proxyHeight: NO_PROXY,
      });
    });
  }

  requestFrame(mediaId: ID, timeSec: number): Promise<ImageBitmap | null> {
    return new Promise<ImageBitmap | null>((resolve) => {
      const reqId = ++this.reqCounter;
      this.pending.set(reqId, (response) => {
        if (response.type === 'frame' && response.ok && response.bitmap) resolve(response.bitmap);
        else resolve(null);
      });
      this.worker.postMessage({ type: 'frame', reqId, mediaId, timeSec });
    });
  }

  closeMedia(mediaId: ID): void {
    this.worker.postMessage({ type: 'close', mediaId });
  }

  disposeSource(assetId: ID): void {
    this.sources.get(assetId)?.dispose();
    this.sources.delete(assetId);
    this.releaseAudio(assetId);
  }

  /** Dispose all sources but keep the worker alive (used when starting a new project). */
  clearSources(): void {
    for (const source of this.sources.values()) source.dispose();
    this.sources.clear();
    for (const assetId of [...this.audioEls.keys()]) this.releaseAudio(assetId);
  }

  dispose(): void {
    for (const source of this.sources.values()) source.dispose();
    this.sources.clear();
    for (const assetId of [...this.audioEls.keys()]) this.releaseAudio(assetId);
    this.worker.terminate();
  }
}

function assetToRecord(
  id: ID,
  file: File,
  kind: 'video' | 'image' | 'audio',
  mimeType: string,
  meta: MediaMeta,
): MediaAsset {
  return {
    id,
    name: file.name || (kind === 'image' ? 'Image' : kind === 'audio' ? 'Audio' : 'Video'),
    kind,
    mimeType,
    durationSec: meta.durationSec,
    width: meta.width,
    height: meta.height,
    fps: meta.fps,
    hasAudio: meta.hasAudio,
    scratchKey: null,
  };
}

function guessMimeFromName(name: string): string {
  const ext = name.toLowerCase().split('.').pop() ?? '';
  if (['mp4', 'm4v', 'mov'].includes(ext)) return 'video/mp4';
  if (['webm', 'mkv'].includes(ext)) return 'video/webm';
  if (['png'].includes(ext)) return 'image/png';
  if (['jpg', 'jpeg'].includes(ext)) return 'image/jpeg';
  if (['gif'].includes(ext)) return 'image/gif';
  if (['webp'].includes(ext)) return 'image/webp';
  if (['mp3', 'wav', 'm4a', 'aac', 'ogg'].includes(ext)) return 'audio/' + ext;
  return 'video/mp4';
}
